import { useEffect, useState } from 'react';
import { C } from '../../theme';
import { generateQbrNarrative, emptyNarrative, buildPriorQuarterPayload } from '../../qbr/narrative';
import { gradeColor, QBR_METRICS } from '../../qbr/grading';

// Step 3 of the QBR flow — Claude-drafted narrative, editable inline.
//
// On mount, auto-fires generateQbrNarrative (so the FMXer doesn't have
// to click a separate "Generate" button). Sections:
//   - Executive Summary    (wins + risks)
//   - Metric Callouts      (one line per metric, with grade pill)
//   - Customer Win         (single highlight)
//   - Mutual Action Plan   (owner + item, add/remove rows)
//   - Roadmap Teaser       (exactly 3 items)
//
// Every text is an editable textarea — the FMXer is expected to tweak
// before generating the deck. A "Regenerate" button re-fires Claude
// (discarding edits, with a confirm dialog so it can't happen by
// accident).
//
// Output (onContinue): { narrative, runId }.
export default function QbrPreviewStep({ project, setup, dataResult, currentProfile, onBack, onContinue }) {
  const [narrative, setNarrative] = useState(() => emptyNarrative());
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [errorRaw,  setErrorRaw]  = useState(null);
  const [runId,     setRunId]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    runGenerate({ cancelled });
    return () => { cancelled = true; };
    // Fire once on mount; runGenerate closes over current props/state.
  }, []); // eslint-disable-line

  async function runGenerate({ cancelled = false } = {}) {
    setLoading(true);
    setError(null);
    setErrorRaw(null);
    try {
      const { narrative: n, runId: id } = await generateQbrNarrative({
        project, setup, dataResult,
        userId: currentProfile?.id,
        // Without this the prompt's PRIOR_QUARTER_JSON was permanently
        // 'null' and Claude could never speak to quarter-over-quarter
        // trend (wr_volume_trend stayed empty by the prompt's own rule).
        priorQuarter: buildPriorQuarterPayload(dataResult?.prior),
      });
      if (cancelled) return;
      // Merge into the empty scaffold so missing keys don't crash the UI.
      setNarrative(mergeNarrative(emptyNarrative(), n));
      setRunId(id);
    } catch (e) {
      if (cancelled) return;
      setError(e.message || 'Narrative generation failed.');
      // generateQbrNarrative attaches the raw model text on parse
      // failures so admins can see exactly what Claude said.
      setErrorRaw(typeof e?.rawText === 'string' ? e.rawText : null);
    } finally {
      if (!cancelled) setLoading(false);
    }
  }

  function handleRegenerate() {
    if (!window.confirm('Regenerate the narrative? Your edits in this step will be lost.')) return;
    runGenerate();
  }

  return (
    <div>
      <Header
        loading={loading}
        onRegenerate={handleRegenerate}
        quarterLabel={setup?.quarterLabel}
      />

      {error && (
        <div style={{
          padding: '10px 14px', borderRadius: 6, fontSize: 12, marginBottom: 14,
          background: C.errBg, color: C.errText, border: `1px solid ${C.errBorder}`,
        }}>
          <div>{error}</div>
          {errorRaw && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                Show raw Claude response ({errorRaw.length} chars)
              </summary>
              <pre style={{
                marginTop: 6, padding: '8px 10px', borderRadius: 4,
                background: '#fff', color: C.textDark, border: `1px solid ${C.errBorder}`,
                fontSize: 11, lineHeight: 1.45,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: 320, overflowY: 'auto',
              }}>{errorRaw}</pre>
            </details>
          )}
        </div>
      )}

      {loading ? (
        <LoadingPlaceholder />
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          <ExecSummarySection
            value={narrative.exec_summary}
            onChange={v => setNarrative(n => ({ ...n, exec_summary: v }))}
          />
          <CalloutsSection
            value={narrative.metric_callouts}
            grades={dataResult?.grades || dataResult?.overall?.grades || {}}
            onChange={v => setNarrative(n => ({ ...n, metric_callouts: v }))}
          />
          <SlideCaptionsSection
            value={narrative.slide_captions}
            onChange={v => setNarrative(n => ({ ...n, slide_captions: v }))}
          />
          <CustomerWinSection
            value={narrative.customer_win}
            onChange={v => setNarrative(n => ({ ...n, customer_win: v }))}
          />
          <AssetHealthSection
            equipment={dataResult?.overall?.topEquipment || dataResult?.topEquipment || []}
            actions={narrative.asset_health}
            onChange={v => setNarrative(n => ({ ...n, asset_health: v }))}
          />
          <MapSection
            value={narrative.mutual_action_plan}
            onChange={v => setNarrative(n => ({ ...n, mutual_action_plan: v }))}
          />
          <RoadmapSection
            value={narrative.roadmap_teaser}
            onChange={v => setNarrative(n => ({ ...n, roadmap_teaser: v }))}
          />
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
        <button onClick={onBack} style={btnSecondary}>← Back</button>
        <button
          onClick={() => onContinue({ narrative, runId })}
          disabled={loading}
          style={{ ...btnPrimary,
            background: loading ? '#D1D5DB' : C.orange,
            cursor:     loading ? 'not-allowed' : 'pointer' }}
        >Continue →</button>
      </div>
    </div>
  );
}

function Header({ loading, onRegenerate, quarterLabel }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 16, gap: 12, flexWrap: 'wrap',
    }}>
      <div>
        <div style={{ fontSize: 11, color: C.textMid, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
          Step 3 · Narrative
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.navy, marginTop: 2 }}>
          {quarterLabel} draft
        </div>
        <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
          Claude has drafted the slide copy from your numbers. Tweak any block before generating the deck.
        </div>
      </div>
      <button
        onClick={onRegenerate}
        disabled={loading}
        style={{
          padding: '8px 14px', fontSize: 12, fontWeight: 600,
          background: '#fff', color: C.navy,
          border: `1px solid ${C.border}`, borderRadius: 6,
          cursor: loading ? 'wait' : 'pointer',
          whiteSpace: 'nowrap',
        }}
      >{loading ? 'Generating…' : '↻ Regenerate'}</button>
    </div>
  );
}

function LoadingPlaceholder() {
  return (
    <div style={{
      padding: 32, borderRadius: 10,
      background: C.navyTint, border: `1px solid ${C.border}`,
      textAlign: 'center', color: C.textMid, fontSize: 13,
    }}>
      Drafting narrative with Claude — this usually takes 10–20 seconds…
    </div>
  );
}

// ----- Sections -----

function Section({ title, accent, subtitle, children }) {
  return (
    <div style={{
      background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10,
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${C.border}`,
        background: '#FAFBFC',
        display: 'flex', alignItems: 'baseline', gap: 10,
      }}>
        <div style={{ width: 3, height: 18, borderRadius: 2, background: accent || C.navy, alignSelf: 'center' }} />
        <div style={{ fontWeight: 700, color: C.navy, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 11, color: C.textMid, fontStyle: 'italic' }}>{subtitle}</div>
        )}
      </div>
      <div style={{ padding: 16, display: 'grid', gap: 10 }}>
        {children}
      </div>
    </div>
  );
}

function ExecSummarySection({ value, onChange }) {
  const wins = value?.wins || [];
  const risks = value?.risks || [];
  return (
    <Section title="Executive Summary" accent={C.orange} subtitle="3 wins, 1–2 risks">
      <BulletList
        label="Wins"
        items={wins}
        min={3} max={5}
        onChange={items => onChange({ ...value, wins: items })}
      />
      <BulletList
        label="Risks"
        items={risks}
        min={1} max={3}
        onChange={items => onChange({ ...value, risks: items })}
      />
    </Section>
  );
}

function CalloutsSection({ value, grades, onChange }) {
  return (
    <Section title="Metric Callouts" subtitle="One or two sentences per metric — what the slide will say">
      <div style={{ display: 'grid', gap: 10 }}>
        {QBR_METRICS.map(m => {
          const grade = grades?.[m.key];
          return (
            <div key={m.key} style={{ display: 'grid', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.navy }}>{m.label}</div>
                <GradePill grade={grade} />
              </div>
              <Textarea
                value={value?.[m.key] || ''}
                onChange={t => onChange({ ...value, [m.key]: t })}
                minRows={2}
                placeholder="What the slide should say about this metric…"
              />
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function CustomerWinSection({ value, onChange }) {
  return (
    <Section title="Customer Win" subtitle="One specific highlight — a milestone, a workflow, a fast resolution">
      <Textarea
        value={value || ''}
        onChange={onChange}
        minRows={2}
        placeholder="A specific moment to call out by name…"
      />
    </Section>
  );
}

function MapSection({ value, onChange }) {
  const items = value || [];
  function update(i, patch) {
    onChange(items.map((it, j) => j === i ? { ...it, ...patch } : it));
  }
  function remove(i) {
    onChange(items.filter((_, j) => j !== i));
  }
  function add(owner) {
    onChange([...items, { owner, item: '' }]);
  }
  return (
    <Section title="Mutual Action Plan" subtitle="3–5 commitments split between FMX and the customer">
      <div style={{ display: 'grid', gap: 8 }}>
        {items.length === 0 && (
          <div style={{ fontSize: 12, color: C.textLight, fontStyle: 'italic' }}>
            No items yet — add one below.
          </div>
        )}
        {items.map((it, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '110px 1fr auto', gap: 8, alignItems: 'flex-start' }}>
            <select
              value={it.owner || 'fmx'}
              onChange={e => update(i, { owner: e.target.value })}
              style={{
                padding: '6px 8px', fontSize: 12, borderRadius: 5,
                border: `1px solid ${C.border}`, background: '#fff',
              }}
            >
              <option value="fmx">FMX</option>
              <option value="customer">Customer</option>
            </select>
            <Textarea
              value={it.item || ''}
              onChange={t => update(i, { item: t })}
              minRows={2}
              placeholder="Concrete commitment for next quarter…"
            />
            <button onClick={() => remove(i)} style={removeBtn} title="Remove this item">×</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => add('fmx')}      style={addBtn}>+ FMX item</button>
          <button onClick={() => add('customer')} style={addBtn}>+ Customer item</button>
        </div>
      </div>
    </Section>
  );
}

function SlideCaptionsSection({ value, onChange }) {
  const v = value || {};
  function set(k, t) { onChange({ ...v, [k]: t }); }
  return (
    <Section title="Slide Captions" subtitle="One-line story sentences that sit under the headline numbers on specific slides">
      <CaptionRow label="Maintenance Activity (slide 7)"   help="Explains the WHY of the headline number — e.g. 'Driven by an increase in scheduled PM volume.'"
        value={v.maintenance_activity || ''} onChange={t => set('maintenance_activity', t)} />
      <CaptionRow label="Planned vs Reactive (slide 10)"   help="A milestone moment — e.g. 'First quarter above 50% since onboarding.'"
        value={v.planned_vs_reactive  || ''} onChange={t => set('planned_vs_reactive',  t)} />
      <CaptionRow label="PM Compliance RECOMMEND (slide 11)" help="Concrete coaching action for the FMXer to follow up on next quarter."
        value={v.pm_recommend         || ''} onChange={t => set('pm_recommend',         t)} />
    </Section>
  );
}

function CaptionRow({ label, help, value, onChange }) {
  return (
    <div style={{ display: 'grid', gap: 3 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.navy }}>{label}</div>
      {help && <div style={{ fontSize: 11, color: C.textMid }}>{help}</div>}
      <Textarea value={value} onChange={onChange} minRows={2} placeholder="…" />
    </div>
  );
}

// equipment[] is the authoritative list (name + locationName + count
// from the data pipeline). actions[] is the parallel list from the
// narrative (Claude-generated recommended_action per row). The FMXer
// can edit only the actions; equipment metadata is read-only.
function AssetHealthSection({ equipment, actions, onChange }) {
  if (!Array.isArray(equipment) || equipment.length === 0) {
    return (
      <Section title="Asset Health (slide 14)" subtitle="Top equipment by work-request volume — one recommended action per row">
        <div style={{ fontSize: 12, color: C.textLight, fontStyle: 'italic' }}>
          No equipment data — work-requests in this period don't reference any equipment items, so the Asset Health slide will show em-dashes.
        </div>
      </Section>
    );
  }
  function setAction(i, text) {
    const next = equipment.map((e, j) => ({
      name: e.name,
      locationName: e.locationName,
      count: e.count,
      recommended_action: j === i
        ? text
        : (actions?.[j]?.recommended_action ?? ''),
    }));
    onChange(next);
  }
  return (
    <Section title="Asset Health (slide 14)" subtitle={`Top ${equipment.length} equipment by work-request volume — Claude suggests an action; tweak per row before generating`}>
      <div style={{ display: 'grid', gap: 8 }}>
        {equipment.map((e, i) => (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 8, alignItems: 'flex-start',
            border: `1px solid ${C.border}`, borderRadius: 6, padding: 10,
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{e.name || '—'}</div>
              <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>
                {e.locationName || '(no building)'}
                {e.count != null && <> · {e.count} request{e.count === 1 ? '' : 's'}</>}
              </div>
            </div>
            <Textarea
              value={actions?.[i]?.recommended_action || ''}
              onChange={v => setAction(i, v)}
              minRows={2}
              placeholder="Recommended action (e.g. 'Schedule end-of-life assessment')"
            />
          </div>
        ))}
      </div>
    </Section>
  );
}

// Convert mixed roadmap_teaser shapes into the canonical
// [{title, description}] array of exactly 3.
function normalizeRoadmapItems(v) {
  const arr = Array.isArray(v) ? v : [];
  const out = [];
  for (let i = 0; i < 3; i++) {
    const r = arr[i];
    if (r && typeof r === 'object') {
      out.push({ title: r.title || '', description: r.description || '' });
    } else if (typeof r === 'string') {
      // Legacy v1 narrative shape — title-only string.
      out.push({ title: r, description: '' });
    } else {
      out.push({ title: '', description: '' });
    }
  }
  return out;
}

function RoadmapSection({ value, onChange }) {
  const items = normalizeRoadmapItems(value);
  function update(i, patch) {
    onChange(items.map((it, j) => j === i ? { ...it, ...patch } : it));
  }
  return (
    <Section title="Roadmap Teaser" subtitle="What's coming in FMX next quarter — exactly 3 items, each with a title + a why-this-customer description">
      <div style={{ display: 'grid', gap: 10 }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '22px 1fr', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ fontSize: 12, color: C.textLight, paddingTop: 6, textAlign: 'right' }}>{i + 1}.</div>
            <div style={{ display: 'grid', gap: 4 }}>
              <input
                type="text"
                value={it.title || ''}
                onChange={e => update(i, { title: e.target.value })}
                placeholder="Title (e.g. Mobile Photo Markup)"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '6px 10px', fontSize: 13, fontWeight: 600,
                  border: `1px solid ${C.border}`, borderRadius: 5,
                  outline: 'none', color: C.navy,
                }}
              />
              <Textarea
                value={it.description || ''}
                onChange={v => update(i, { description: v })}
                minRows={2}
                placeholder="Why this customer in particular will care…"
              />
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ----- Primitives -----

function BulletList({ label, items, min, max, onChange }) {
  function update(i, t) {
    onChange(items.map((it, j) => j === i ? t : it));
  }
  function remove(i) {
    if (items.length <= min) return;
    onChange(items.filter((_, j) => j !== i));
  }
  function add() {
    if (max != null && items.length >= max) return;
    onChange([...items, '']);
  }
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.textMid, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {items.map((t, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '22px 1fr auto', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ fontSize: 12, color: C.textLight, paddingTop: 6, textAlign: 'right' }}>•</div>
            <Textarea value={t} onChange={v => update(i, v)} minRows={1} placeholder="…" />
            {items.length > min && (
              <button onClick={() => remove(i)} style={removeBtn} title="Remove">×</button>
            )}
          </div>
        ))}
        {(max == null || items.length < max) && (
          <button onClick={add} style={addBtn}>+ Add</button>
        )}
      </div>
    </div>
  );
}

function Textarea({ value, onChange, minRows = 2, placeholder }) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={minRows}
      placeholder={placeholder}
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: '6px 10px', fontSize: 12, lineHeight: 1.5,
        border: `1px solid ${C.border}`, borderRadius: 5,
        outline: 'none', resize: 'vertical',
        fontFamily: 'inherit', color: C.textDark,
        background: '#fff',
      }}
    />
  );
}

function GradePill({ grade }) {
  if (!grade) return (
    <div style={{
      fontSize: 10, padding: '2px 8px', borderRadius: 8,
      background: '#F3F4F6', color: C.textLight, fontWeight: 700,
    }}>—</div>
  );
  return (
    <div style={{
      fontSize: 10, padding: '2px 8px', borderRadius: 8,
      background: gradeColor(grade), color: '#fff', fontWeight: 700,
    }}>{grade}</div>
  );
}

// Merge a Claude response over the empty scaffold so the UI never crashes
// on missing keys. Arrays / objects from Claude take precedence; missing
// fields fall back to the scaffold defaults.
function mergeNarrative(scaffold, n) {
  return {
    exec_summary: {
      wins:  Array.isArray(n?.exec_summary?.wins)  ? n.exec_summary.wins  : scaffold.exec_summary.wins,
      risks: Array.isArray(n?.exec_summary?.risks) ? n.exec_summary.risks : scaffold.exec_summary.risks,
    },
    metric_callouts: { ...scaffold.metric_callouts, ...(n?.metric_callouts || {}) },
    slide_captions:  { ...scaffold.slide_captions,  ...(n?.slide_captions  || {}) },
    customer_win: typeof n?.customer_win === 'string' ? n.customer_win : scaffold.customer_win,
    asset_health: Array.isArray(n?.asset_health)
      ? n.asset_health.map(it => ({
          name: it?.name || '',
          locationName: it?.locationName || it?.location || '',
          count: it?.count ?? null,
          recommended_action: it?.recommended_action || it?.action || '',
        }))
      : scaffold.asset_health,
    mutual_action_plan: Array.isArray(n?.mutual_action_plan) ? n.mutual_action_plan : scaffold.mutual_action_plan,
    roadmap_teaser: normalizeRoadmapItems(n?.roadmap_teaser ?? scaffold.roadmap_teaser),
  };
}

const addBtn = {
  padding: '5px 10px', fontSize: 11, fontWeight: 600,
  background: '#fff', color: C.navy,
  border: `1px dashed ${C.border}`, borderRadius: 5,
  cursor: 'pointer', alignSelf: 'flex-start',
};
const removeBtn = {
  padding: '4px 9px', fontSize: 14, fontWeight: 600, lineHeight: 1,
  background: 'transparent', color: C.textLight,
  border: 'none', cursor: 'pointer', alignSelf: 'flex-start',
};
const btnPrimary = {
  padding: '10px 24px', fontSize: 14, fontWeight: 600,
  color: '#fff', border: 'none', borderRadius: 6,
};
const btnSecondary = {
  padding: '8px 16px', fontSize: 13, fontWeight: 500,
  background: '#fff', color: C.textDark,
  border: `1px solid ${C.border}`, borderRadius: 6, cursor: 'pointer',
};
